class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]') ?? this.querySelector("#fp-recently-viewed");
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.minimumSliderItemsToStartInfiniteScroll = 7;
    this.enableSliderLooping = false;
    this.isLooping = false; // Prevent multiple simultaneous loops
    this.enableInfiniteLoop = this.hasAttribute("data-enable-infinite-loop");
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.sliderIndicatorActive = this.querySelector(".fp-pdp-carousel__indicator-active");
    this.sliderIndicatorWrapper = this.querySelector(".fp-pdp__slider-buttons--bar");
    this.onButtonClick = this.onButtonClick.bind(this);
    this.onIndicatorClick = this.onIndicatorClick.bind(this);
    this.initPages();
  }

  connectedCallback() {
    this.allMediaGalleries = document.querySelectorAll("media-gallery");
    this.mediaGallery = this.allMediaGalleries[0]; // PDP media gallery on the left
    this.productModalMediaGallery = this.allMediaGalleries[1]; // Modal opened when clicking on PDP media gallery

    if (!this.slider || !this.nextButton) return;

    this.resizeObserver = new ResizeObserver((_entries) => {
      this.resetPages();
    });
    this.resizeObserver.observe(this.slider);

    this.debouncedScrollHandler = this.debounce(() => {
      this.update();
    }, 16);

    this.update = this.update.bind(this);
    this.slider.addEventListener("scroll", this.debouncedScrollHandler);
    this.prevButton.addEventListener("click", this.onButtonClick);
    this.nextButton.addEventListener("click", this.onButtonClick);

    if (this.sliderIndicatorWrapper) {
      this.sliderIndicatorWrapper.addEventListener("click", this.onIndicatorClick);
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (!this.slider || !this.nextButton) return;

    this.slider.removeEventListener("scroll", this.debouncedScrollHandler);
    this.prevButton.removeEventListener("click", this.onButtonClick);
    this.nextButton.removeEventListener("click", this.onButtonClick);

    if (this.sliderIndicatorWrapper) {
      this.sliderIndicatorWrapper.removeEventListener("click", this.onIndicatorClick);
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.ceil(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.enableInfiniteLoop =
      this.enableInfiniteLoop && this.sliderItems.length >= this.minimumSliderItemsToStartInfiniteScroll;
    if (this.enableInfiniteLoop) {
      this.initInfiniteLoop();
      // Enable buttons for carousel
      this.prevButton.removeAttribute("disabled");
      this.nextButton.removeAttribute("disabled");
    }
    this.update();
  }

  initInfiniteLoop() {
    if (!this.clonesAppended) {
      const [firstItems, lastItems] = this.createClonedSlides();
      this.slider.appendChild(firstItems);
      this.slider.prepend(lastItems);
      this.clonesAppended = true; // we only need to do this once
    }

    // Update clone positions
    this.updateClonePositions();
  }

  updateClonePositions() {
    const firstClone = this.slider.querySelector("[data-first-item]");
    const lastClone = this.slider.querySelector("[data-last-item]");

    if (firstClone && lastClone) {
      // Use offsetLeft for more reliable positioning
      this.firstCloneScrollLeft = firstClone.offsetLeft;
      this.lastCloneScrollLeft = lastClone.offsetLeft;

      // Calculate the exact scroll positions for seamless looping
      // Position to jump to when scrolling forward past the last item
      this.originalStartScroll = this.lastCloneScrollLeft + this.sliderItemOffset;

      // Position to jump to when scrolling backward past the first item
      this.originalEndScroll = this.firstCloneScrollLeft - this.sliderItemOffset;
    }
  }

  createClonedSlides() {
    const firstSetOfFragments = document.createDocumentFragment();
    const lastSetOfFragments = document.createDocumentFragment();

    this.sliderItems.forEach((item, index) => {
      if (index <= this.slidesPerPage) {
        const clone = item.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.removeAttribute("id"); // Remove duplicate IDs
        if (index === 0) {
          clone.setAttribute("data-first-item", "true");
        }
        firstSetOfFragments.appendChild(clone);
      }
      if (index >= this.sliderItems.length - (this.slidesPerPage + 1)) {
        const clone = item.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.removeAttribute("id"); // Remove duplicate IDs
        if (index === this.sliderItems.length - 1) {
          clone.setAttribute("data-last-item", "true");
        }
        lastSetOfFragments.appendChild(clone);
      }
    });

    return [firstSetOfFragments, lastSetOfFragments];
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    this.updateIndicator();

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    if (this.enableSliderLooping) return;

    // Infinite loop logic on scroll
    if (this.enableInfiniteLoop && !this.isLooping) {
      this.handleInfiniteLoopScroll();
      return;
    }

    if (this.slider.getAttribute("id")?.includes("Slider-ProductModal-Thumbnails") || this.enableInfiniteLoop) {
      // Don't disable slider buttons on product modal so that we can have infinite looping
      return;
    } else {
      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
        this.prevButton.setAttribute("disabled", "disabled");
      } else {
        this.prevButton.removeAttribute("disabled");
      }

      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
        this.nextButton.setAttribute("disabled", "disabled");
      } else {
        this.nextButton.removeAttribute("disabled");
      }
    }
  }

  updateIndicator() {
    if (!this.sliderIndicatorActive) return;
    const slideCount = this.sliderItemsToShow?.length || 1;
    this.sliderIndicatorActive.style.width = `${100 / slideCount}%`;
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    const progress = maxScroll > 0 ? this.slider.scrollLeft / maxScroll : 0;
    const clampedProgress = Math.min(Math.max(progress, 0), 1);
    this.sliderIndicatorActive.style.transform = `translateX(${clampedProgress * (slideCount - 1) * 100}%)`;
  }

  onIndicatorClick(event) {
    event.stopPropagation();
    if (!this.sliderIndicatorWrapper || !this.sliderItemsToShow || this.sliderItemsToShow.length < 2) return;
    const rect = this.sliderIndicatorWrapper.getBoundingClientRect();
    if (rect.width === 0) return;
    const fraction = (event.clientX - rect.left) / rect.width;
    const slideCount = this.sliderItemsToShow.length;
    const index = Math.min(Math.max(Math.floor(fraction * slideCount), 0), slideCount - 1);
    const target = this.sliderItemsToShow[index];
    if (!target) return;
    this.slider.scrollTo({ left: target.offsetLeft, behavior: "smooth" });
  }

  handleInfiniteLoopScroll() {
    const scrollLeft = this.slider.scrollLeft;
    // Calculate buffer zone - snap earlier to prevent blank space
    const bufferZone = this.sliderItemOffset * 0.8;

    // Check if we've scrolled past the end (into the cloned beginning)
    // Snap before reaching the actual clone to prevent visual gap
    if (scrollLeft >= this.firstCloneScrollLeft - bufferZone) {
      this.snapToPosition(this.originalStartScroll);
      return;
    }

    // Check if we've scrolled before the beginning (into the cloned end)
    // Snap before reaching the actual clone to prevent visual gap
    if (scrollLeft <= this.lastCloneScrollLeft + bufferZone) {
      this.snapToPosition(this.originalEndScroll);
      return;
    }
  }

  snapToPosition(position) {
    if (this.isLooping) return; // Prevent multiple snaps

    this.isLooping = true;

    this.slider.style.scrollBehavior = "auto";
    this.slider.scrollLeft = position;

    // Use minimal delay to re-enable smooth scrolling
    requestAnimationFrame(() => {
      this.slider.style.scrollBehavior = "";
      // Allow new loops after a brief moment
      setTimeout(() => {
        this.isLooping = false;
      }, 100);
    });
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element?.offsetLeft + element?.clientWidth <= lastVisibleSlide && element?.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    if (event.currentTarget.classList.contains("slider-counter__link--dots") && this.mediaGallery) {
      this.mediaGallery.setActiveMedia(event.currentTarget.dataset.target);
      return;
    }

    // Handle product modal infinite looping
    if (event.currentTarget.classList.contains("fp-product-modal__slider-button")) {
      this.handleProductModalInfiniteLoop(event);
      return;
    }

    // Calculate next scroll position
    const step = parseInt(event.currentTarget.dataset.step) || 1;
    const direction = event.currentTarget.name === "next" ? 1 : -1;
    const targetScroll = this.slider.scrollLeft + direction * step * this.sliderItemOffset;

    // Handle infinite loop for buttons
    if (this.enableInfiniteLoop) {
      this.handleInfiniteLoopButton(targetScroll, direction);
    } else {
      this.setSlidePosition(targetScroll);
    }
  }

  handleInfiniteLoopButton(targetScroll, direction) {
    const bufferZone = this.sliderItemOffset * 0.5;

    // Going forward past the last real item
    if (direction > 0 && targetScroll >= this.firstCloneScrollLeft - bufferZone) {
      // Instantly jump without showing the clone
      this.snapToPosition(this.originalStartScroll);
      return;
    }

    // Going backward past the first real item
    if (direction < 0 && targetScroll <= this.lastCloneScrollLeft + bufferZone) {
      this.snapToPosition(this.originalEndScroll);
      return;
    }

    // Normal scroll within bounds
    this.setSlidePosition(targetScroll);
  }

  handleProductModalInfiniteLoop(event) {
    const previousMediaPosition = this.productModalMediaGallery.querySelector(
      '.thumbnail-list__item:has(button[aria-current="true"])',
    )?.dataset.mediaPosition;

    if (!previousMediaPosition) return;

    let nextMediaPosition;
    const thumbnails = this.productModalMediaGallery.querySelectorAll(".thumbnail-list__item");
    const totalThumbnails = thumbnails.length;

    if (event.currentTarget.name === "next") {
      nextMediaPosition = previousMediaPosition == totalThumbnails ? "1" : String(+previousMediaPosition + 1);
    } else {
      nextMediaPosition = previousMediaPosition == "1" ? String(totalThumbnails) : String(+previousMediaPosition - 1);
    }

    const nextMedia = this.productModalMediaGallery.querySelector(
      `.thumbnail-list__item[data-media-position="${nextMediaPosition}"]`,
    );

    if (nextMedia) {
      this.productModalMediaGallery.setActiveMedia(nextMedia.dataset.target);

      // Handle scroll position for looping
      if (previousMediaPosition == "1" && event.currentTarget.name === "previous") {
        this.setSlidePosition(99999);
      } else if (previousMediaPosition == totalThumbnails && event.currentTarget.name === "next") {
        this.setSlidePosition(0);
      }
    }
  }

  setSlidePosition(position, smooth = true, callback = null) {
    if (!smooth) {
      this.slider.style.scrollBehavior = "auto";
    }

    this.slider.scrollTo({
      left: position,
    });

    if (!smooth) {
      setTimeout(() => {
        this.slider.style.scrollBehavior = "";
        if (callback) callback();
      }, 50);
    } else if (callback) {
      callback();
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

customElements.define("slider-component", SliderComponent);